import { ChangeDetectionStrategy, Component, OnInit, HostListener, ElementRef, signal, computed, inject } from '@angular/core';

import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { NotificationService } from '../../shared/services/notification/notification.service';
import { EntryInfo } from '../../shared/interfaces/rt-messaging.generated';
import { parseServerDate } from '../../shared/helpers/date-formatting.helper';

type FilterTab = 'all' | 'jobs' | 'invites';

@Component({
  selector: 'app-notification-inbox',
  templateUrl: './notification-inbox.component.html',
  styleUrls: ['./notification-inbox.component.scss'],
  standalone: true,
  imports: [RouterModule, MatIconModule, MatButtonModule, MatBadgeModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationInboxComponent implements OnInit {
  private readonly elementRef = inject(ElementRef);
  readonly notificationService = inject(NotificationService);

  isOpen = signal(false);
  activeTab = signal<FilterTab>('all');
  private readonly initialized = false;

  readonly filteredEntries = computed(() => {
    const entries = this.notificationService.entries();
    const tab = this.activeTab();
    switch (tab) {
      case 'jobs':
        return entries.filter((e) => this.isJobEntry(e));
      case 'invites':
        return entries.filter((e) => this.isInviteEntry(e));
      default:
        return entries;
    }
  });

  ngOnInit(): void {
    this.notificationService.refreshUnseenCount();
  }

  toggleDropdown(): void {
    this.isOpen.set(!this.isOpen());
    if (this.isOpen()) {
      this.loadEntries();
    }
  }

  closeDropdown(): void {
    this.isOpen.set(false);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeDropdown();
  }

  setTab(tab: FilterTab): void {
    this.activeTab.set(tab);
  }

  async markAllRead(): Promise<void> {
    await this.notificationService.markAllSeen();
  }

  getEntryIcon(entry: EntryInfo): string {
    if (this.isInviteEntry(entry)) return 'person_add';
    if (this.isJobEntry(entry)) return 'play_circle';
    return 'notifications';
  }

  getTimeAgo(dateStr: string): string {
    const date = parseServerDate(dateStr);
    if (!date) return '—';
    const now = new Date();
    // Clamp at 0: a naive-UTC timestamp parsed in a west-of-UTC browser can
    // land slightly in the future, which would otherwise produce a negative
    // diff and always render "Just now" (EHQ-66).
    const diffMs = Math.max(0, now.getTime() - date.getTime());
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  }

  getProjectName(entry: EntryInfo): string | null {
    const meta = entry.metadata;
    if (meta?.['project_name']) return String(meta['project_name']);
    if (entry.entryProjectId) return `Project ${entry.entryProjectId}`;
    return null;
  }

  private async loadEntries(): Promise<void> {
    await this.notificationService.loadEntries({ limit: 50 });
  }

  private isInviteEntry(entry: EntryInfo): boolean {
    const name = (entry.eventName || '').toLowerCase();
    const channel = (entry.channelName || '').toLowerCase();
    return name.includes('invite') || channel.includes('invite');
  }

  private isJobEntry(entry: EntryInfo): boolean {
    const name = (entry.eventName || '').toLowerCase();
    const channel = (entry.channelName || '').toLowerCase();
    return (
      name.includes('job') ||
      channel.includes('job') ||
      name.includes('routine') ||
      channel.includes('routine')
    );
  }
}
